// FIN-1154 PE-R14: what a consultant page view records about the page. Only the route template
// (ids replaced), its area (PE-R9's route groups) and the object on screen are kept; the raw
// path and query string never leave this module. product-events-api validates the same shapes
// (supabase/functions/_shared/services/productEvents/productPageEventService.ts).

type PageArea =
  | 'overview' | 'documents' | 'journal_entries' | 'matching' | 'payables' | 'receivables'
  | 'messages' | 'todos' | 'tax' | 'settings' | 'other';

type PageEntityType = 'bookkeeping_suggestion' | 'bank_account' | 'tax_account';

export interface PageDescription {
  route: string;
  area: PageArea;
  entityType: PageEntityType | null;
  entityId: string | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The workspace routes in App.tsx, by area. */
const STATIC_ROUTES: Record<string, PageArea> = {
  '/overview': 'overview',
  '/review': 'documents',
  '/upload': 'documents',
  '/invoice-upload': 'documents',
  '/receipts': 'documents',
  '/expenses': 'documents',
  '/unmatched-emails': 'documents',
  '/bookkeeping/journal': 'journal_entries',
  '/transactions': 'matching',
  '/bank': 'matching',
  '/cash-management': 'matching',
  '/account-payables': 'payables',
  '/account-payables/payments': 'payables',
  '/account-payables/approvals': 'payables',
  '/account-payables-approvals': 'payables',
  '/payments': 'payables',
  '/account-receivables': 'receivables',
  '/create-invoice': 'receivables',
  '/invoices': 'receivables',
  '/sales-reports': 'receivables',
  '/messages': 'messages',
  '/todos': 'todos',
  '/profile': 'settings',
  '/account-billing': 'settings',
  '/settings': 'settings',
  '/user-permissions': 'settings',
  '/user-management': 'settings',
};

const ENTITY_ROUTES: Array<{ prefix: string; route: string; area: PageArea; entityType: PageEntityType }> = [
  { prefix: '/bank-account/', route: '/bank-account/:id', area: 'matching', entityType: 'bank_account' },
  { prefix: '/tax-account/', route: '/tax-account/:id', area: 'tax', entityType: 'tax_account' },
];

/** Sign-in, invitation, onboarding and OAuth callback pages: not consultant work on a company. */
const OUTSIDE_WORKSPACE = [
  '/', '/auth', '/auth/confirm', '/accept-invite', '/bank/callback', '/tax/callback',
  '/join-company', '/user-onboarding', '/onboarding-steps', '/erp/callback', '/gmail/callback',
];

export function describePage(pathname: string, search: string): PageDescription | null {
  let path = pathname;
  while (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (OUTSIDE_WORKSPACE.includes(path)) return null;

  const area = STATIC_ROUTES[path];
  if (area) {
    if (path === '/review') {
      const suggestion = new URLSearchParams(search).get('suggestion');
      if (suggestion && UUID.test(suggestion)) {
        return { route: path, area, entityType: 'bookkeeping_suggestion', entityId: suggestion.toLowerCase() };
      }
    }
    return { route: path, area, entityType: null, entityId: null };
  }

  for (const entityRoute of ENTITY_ROUTES) {
    if (!path.startsWith(entityRoute.prefix)) continue;
    const id = path.slice(entityRoute.prefix.length);
    if (UUID.test(id)) {
      return { route: entityRoute.route, area: entityRoute.area, entityType: entityRoute.entityType, entityId: id.toLowerCase() };
    }
  }

  return { route: '*', area: 'other', entityType: null, entityId: null };
}
